import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import {
  borderGrey,
  customError,
  darkPurple,
  lightGrey,
  warningYellow,
} from '../../../mainTheme';

interface Props {
  title: string;
  subtitle?: string;
  /** show the amber "Mock data" badge when the metric has no backend yet */
  mocked?: boolean;
  loading?: boolean;
  error?: string | null;
  empty?: boolean;
  emptyText?: string;
  minHeight?: number | string;
  action?: React.ReactNode;
  children: React.ReactNode;
}

/** Card wrapper for a dashboard panel: header + mock badge + loading/error/empty states. */
const ChartPanel: React.FC<Props> = ({
  title,
  subtitle,
  mocked,
  loading,
  error,
  empty,
  emptyText = 'No data for this range',
  minHeight = 220,
  action,
  children,
}) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${borderGrey}`,
      borderRadius: '16px',
      p: 2.5,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      mb={1.5}
    >
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: darkPurple }}
          >
            {title}
          </Typography>
          {mocked && (
            <Chip
              label="Mock data"
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                color: '#7A5A00',
                backgroundColor: '#FFF4CC',
                border: `1px solid ${warningYellow}`,
              }}
              title="No backend yet — served from the mock adapter"
            />
          )}
        </Box>
        {subtitle && (
          <Typography variant="caption" sx={{ color: lightGrey }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>

    <Box
      flex={1}
      minHeight={minHeight}
      display="flex"
      alignItems={loading || error || empty ? 'center' : 'stretch'}
      justifyContent={loading || error || empty ? 'center' : 'flex-start'}
      flexDirection="column"
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <Typography variant="body2" sx={{ color: customError }}>
          {error}
        </Typography>
      ) : empty ? (
        <Typography variant="body2" sx={{ color: lightGrey }}>
          {emptyText}
        </Typography>
      ) : (
        children
      )}
    </Box>
  </Paper>
);

export default ChartPanel;
